<template>
  <!-- eslint-disable vue/no-v-html-->
  <wizard
    ref="wizard"
    v-slot="{ currentStep }"
    first-step="personalDetails"
    class="wizard-application"
  >
    <wizard-step-intro>
      <h2 v-if="i18n.wizardSteps.wizardApplication.title">
        {{ i18n.wizardSteps.wizardApplication.title }}
      </h2>
      <slot name="wizard-application-paragraph" />
    </wizard-step-intro>

    <wizard-step-indicator
      :steps="steps"
      :current-step="currentStep"
    />

    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      next-step="myChild"
      :gtm="triggerGtm"
    >
      <wizard-step-intro>
        <h3 v-if="i18n.wizardSteps.personalDetails.title">
          {{ i18n.wizardSteps.personalDetails.title }}
        </h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <FormulateInput
            name="salutation"
            :options="{
              male: i18n.form.labels.salutationOptions.male,
              female: i18n.form.labels.salutationOptions.female,
            }"
            type="radio"
            :label="i18n.form.labels.salutation"
            validation="required"
            :validation-messages="i18n.form.validations.salutation"
          />
        </div>
        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="bonusCode"
            type="text"
            label="bonus code"
            validation="nohtml"
            class="custom-form-element"
            aria-hidden="true"
          />
          <FormulateInput
            name="website"
            type="text"
            label="website"
            validation="nohtml"
            class="custom-form-element"
            aria-hidden="true"
          />
          <FormulateInput
            :label="i18n.form.labels.initials"
            :validation-name="i18n.form.labels.initials"
            name="initials"
            validation="bail|required:trim|nohtml|max:12,length"
            :validation-messages="i18n.form.validations.initials"
          />
        </div>
        <div class="col-span-12 md:col-span-6" />
        <div class="col-span-12 md:col-span-7">
          <FormulateInput
            :label="i18n.form.labels.firstName"
            :validation-name="i18n.form.labels.firstName"
            name="firstname"
            validation="bail|required:trim|nohtml|max:50,length"
            :validation-messages="i18n.form.validations.firstName"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            :label="i18n.form.labels.prefix"
            :validation-name="i18n.form.labels.prefix"
            name="prefix"
            validation="bail|nohtml|max:25,length"
            :validation-messages="i18n.form.validations.prefix"
          />
        </div>
        <div class="col-span-12 md:col-span-12">
          <FormulateInput
            :label="i18n.form.labels.lastName"
            :validation-name="i18n.form.labels.lastName"
            name="lastname"
            validation="bail|required:trim|nohtml|max:50,length"
            :validation-messages="i18n.form.validations.lastName"
          />
        </div>
        <div class="col-span-12 md:col-span-7">
          <FormulateInput
            name="email"
            type="email"
            :label="i18n.form.labels.email"
            validation="bail|required:trim|email|max:255,length"
            :validation-messages="i18n.form.validations.email"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            name="phoneNumber"
            type="text"
            :label="i18n.form.labels.phoneNumber"
            validation="bail|required:trim|phonenumber|nohtml"
            :validation-messages="i18n.form.validations.phoneNumber"
          />
        </div>
        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            :label="i18n.form.labels.streetName"
            :validation-name="i18n.form.labels.streetName"
            name="streetname"
            validation="bail|required:trim|nohtml|max:75,length"
            :validation-messages="i18n.form.validations.streetname"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.houseNumber"
            :validation-name="i18n.form.labels.houseNumber"
            name="housenumber"
            validation="bail|required:trim|number|nohtml|max:15,length"
            :validation-messages="{
              required: i18n.form.validations.houseNumber.required,
              number: i18n.form.validations.houseNumber.required,
            }"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.suffix"
            :validation-name="i18n.form.labels.suffix"
            name="suffix"
            validation="bail|nohtml|max:10,length"
            :validation-messages="i18n.form.validations.suffix"
          />
        </div>
        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="zipcode"
            type="text"
            :label="i18n.form.labels.zipcode"
            validation="bail|required:trim|zipcode|nohtml"
            :validation-messages="i18n.form.validations.zipcode"
          />
        </div>
        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="residence"
            :label="i18n.form.labels.residence"
            :validation-name="i18n.form.labels.residence"
            type="text"
            validation="bail|required:trim|nohtml|max:50,length"
            :validation-messages="i18n.form.validations.residence"
          />
        </div>
        <div
          v-if="childcareType.childcareTypeKey === 'Preschool'"
          class="col-span-12"
        >
          <FormulateInput
            name="employed"
            :options="{
              true: i18n.form.labels.employedOptions.yes,
              false: i18n.form.labels.employedOptions.no,
            }"
            type="radio"
            :label="i18n.form.labels.employed"
            validation="required"
            :validation-messages="i18n.form.validations.employed"
          />
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="myChild"
      :model="formData"
      previous-step="personalDetails"
      next-step="preferences"
      :gtm="triggerGtm"
    >
      <wizard-step-intro>
        <h3 v-if="i18n.wizardSteps.myChild.title">
          {{ i18n.wizardSteps.myChild.title }}
        </h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <fields-gender :i18n="i18n" />
        </div>

        <div class="col-span-12 md:col-span-4">
          <FormulateInput
            :label="i18n.form.labels.childFirstName"
            :validation-name="i18n.form.labels.childFirstName"
            name="firstName"
            :validation="
              `bail|${
                childcareType.childcareTypeKey !== 'Daycare' ? 'required:trim|' : ''
              }nohtml|max:50,length`
            "
            :validation-messages="i18n.form.validations.childFirstName"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.childNamePrefix"
            :validation-name="i18n.form.labels.childNamePrefix"
            name="namePrefix"
            validation="nohtml||max:25,length"
            :validation-messages="i18n.form.validations.childNamePrefix"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            :label="i18n.form.labels.childLastName"
            :validation-name="i18n.form.labels.childLastName"
            name="lastName"
            validation="bail|required:trim|nohtml|max:50,length"
            :validation-messages="i18n.form.validations.childLastName"
          />
        </div>

        <!-- DATEPICKER -->
        <div class="col-span-12">
          <fields-date-picker
            v-if="childcareType.childcareTypeKey === 'After school care'"
            field-name="dateOfBirth"
            :date-validation="datePickerValidationAfterSchoolCare"
            :validation-messages-override="{after: i18n.form.labels.dateOfBirthExceedsMaxAge}"
            :i18n="i18n"
          />
          <fields-date-picker
            v-else
            field-name="dateOfBirth"
            :date-validation="datePickerValidationOthers"
            :i18n="i18n"
          />

          <!-- form message for After School care (BSO) date picker -->
          <div
            v-if="
              childcareType.childcareTypeKey !== 'After school care' &&
                filledInDateOfBirthIsMoreThan4YearsAgo
            "
            class="mb-7 text-danger"
            v-html="i18n.form.labels.dateOfBirthTooOldAlternative"
          />

          <div
            v-if="childcareType.childcareTypeKey === 'After school care'"
            class="col-span-12"
          >
            <FormulateInput
              :label="i18n.form.labels.school"
              name="school"
              validation="bail|required:trim|nohtml|max:50,length"
              :validation-messages="i18n.form.validations.school"
            />
          </div>

          <div
            v-if="childcareType.childcareTypeKey === 'Preschool'"
            class="col-span-12"
          >
            <FormulateInput
              :label="i18n.form.labels.bsn"
              name="bsn"
              validation="bail|required:trim|bsn|nohtml"
              :validation-messages="i18n.form.validations.bsn"
            />

            <FormulateInput
              name="vve"
              :options="{
                true: i18n.form.labels.vveOptions.yes,
                false: i18n.form.labels.vveOptions.no,
              }"
              type="radio"
              :label="i18n.form.labels.vve"
              validation="required"
              :validation-messages="i18n.form.validations.vve"
            />
          </div>
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="preferences"
      :model="formData"
      :next-step="sendRequest"
      previous-step="myChild"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h3 v-if="i18n.wizardSteps.preferences.title">
          {{ i18n.wizardSteps.preferences.title }}
        </h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <fields-date-picker
            field-name="startOfCare"
            :min-date="startOfCareMinDate"
            :date-validation="startOfCareValidation"
            :i18n="i18n"
          />

          <fieldset v-if="childcareType.childcareTypeKey === 'After school care'">
            <FormulateInput
              v-model="formData.vsoSupport"
              :class="onlyPreschoolSelected ? 'mb-0' : 'mb-6'"
              :label="i18n.wizardSteps.preferences.vsoSupport.label"
              name="vsoSupport"
              type="checkbox"
              validation="required"
              :options="showVso"
              :validation-messages="i18n.form.validations.vsoSupport"
            />
            <div
              v-if="onlyPreschoolSelected"
              class="mb-6 formulate-input-help formulate-input-help--after"
            >
              {{ i18n.wizardSteps.preferences.vsoSupport.message }}
            </div>
          </fieldset>

          <FormulateInput
            name="days[]"
            :label="i18n.form.labels.preferredday"
            :options="daysOptions"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.days"
          />
          <h3
            v-if="i18n.form.labels.remark"
          >
            {{ i18n.form.labels.remark }}
          </h3>

          <FormulateInput
            name="remark"
            :label="i18n.form.labels.remarkDescripton"
            :validation-name="i18n.form.labels.remarkValidationName"
            type="textarea"
            validation="nohtml|max:4099,length"
            :validation-messages="i18n.form.validations.remark"
          />
        </div>
        <div
          v-if="allowAlternativeLocations"
          class="col-span-12"
        >
          <button
            class="toggle-alternatives w-full block mb-8 text-xl font-bold text-left"
            :class="{ 'is-open': showAlternativePreferences }"
            @click.prevent="toggleAlternativePreferences"
            @keyup.prevent="toggleAlternativePreferences"
          >
            {{ i18n.form.labels.preferenceButton }}
            <chevron-down class="icon" />
          </button>

          <transition
            name="slide-down"
            @before-enter="beforeEnter"
            @enter="enter"
            @before-leave="beforeLeave"
            @leave="leave"
          >
            <div
              v-if="showAlternativePreferences"
              class="grid grid-cols-12 gap-x-8 pb-10"
            >
              <div class="col-span-12 mb-8">
                <slot name="alternative-preferences-paragraph" />
              </div>
              <div class="col-span-12">
                <fieldset>
                  <legend class="mb-4">
                    {{ i18n.form.labels.secondPreference }}
                  </legend>
                  <div class="grid grid-cols-12 gap-x-8">
                    <div class="col-span-12 md:col-span-6">
                      <fieldset>
                        <FormulateInput
                          type="select"
                          name="secondPreferenceCity"
                          :options="filteredCityOptions(formData.preferences.secondPreferenceCity)"
                          :label="i18n.form.labels.preferenceCity"
                          aria-describedby="selectHelp-secondPreferenceCity"
                          @change="onchangeSecondPreferenceCity"
                        />
                        <small
                          v-if="i18n.form.wcag.selectScreenReaderHelp"
                          id="selectHelp-secondPreferenceCity"
                          class="sr-only"
                        >
                          {{ i18n.form.wcag.selectScreenReaderHelp }}
                        </small>
                        <kf-loader
                          v-if="isLoading"
                          loader-size="sm"
                          loader-color="primary"
                        />
                      </fieldset>
                    </div>

                    <div class="col-span-12 md:col-span-6">
                      <fieldset>
                        <FormulateInput
                          type="select"
                          name="secondPreferenceLocation"
                          :options="alternativeLocationOptions.second"
                          :label="i18n.form.labels.preferenceLocation"
                          :validation="requireSecondOption"
                          :validation-messages="i18n.form.validations.school"
                          aria-describedby="selectHelp-secondPreferenceLocation"
                          @change="onChangeSecondPreferedLocation"
                        />
                        <small
                          v-if="i18n.form.wcag.selectScreenReaderHelp"
                          id="selectHelp-secondPreferenceLocation"
                          class="sr-only"
                        >
                          {{ i18n.form.wcag.selectScreenReaderHelp }}
                        </small>
                        <kf-loader
                          v-if="isLoading"
                          loader-size="sm"
                          loader-color="primary"
                        />
                      </fieldset>
                    </div>
                  </div>
                </fieldset>
              </div>

              <div class="col-span-12">
                <fieldset>
                  <legend class="mb-4">
                    {{ i18n.form.labels.thirdPreference }}
                  </legend>
                  <div class="grid grid-cols-12 gap-x-8">
                    <div class="col-span-12 md:col-span-6 mb-4">
                      <fieldset>
                        <FormulateInput
                          :class="`${!formData.preferences.secondPreferenceLocation && 'opacity-50'}`"
                          :disabled="!formData.preferences.secondPreferenceLocation"
                          type="select"
                          name="thirdPreferenceCity"
                          :options="filteredCityOptions(formData.preferences.thirdPreferenceCity)"
                          :label="i18n.form.labels.preferenceCity"
                          aria-describedby="selectHelp-thirdPreferenceCity"
                          @change="onchangeThirdPreferenceCity"
                        />
                        <small
                          v-if="i18n.form.wcag.selectScreenReaderHelp"
                          id="selectHelp-thirdPreferenceCity"
                          class="sr-only"
                        >
                          {{ i18n.form.wcag.selectScreenReaderHelp }}
                        </small>
                        <kf-loader
                          v-if="isLoading"
                          loader-size="sm"
                          loader-color="primary"
                        />
                      </fieldset>
                    </div>

                    <div class="col-span-12 md:col-span-6">
                      <fieldset>
                        <FormulateInput
                          :class="`${!formData.preferences.secondPreferenceLocation && 'opacity-50'}`"
                          :disabled="!formData.preferences.secondPreferenceLocation"
                          type="select"
                          name="thirdPreferenceLocation"
                          :options="alternativeLocationOptions.third"
                          :label="i18n.form.labels.preferenceLocation"
                          aria-describedby="selectHelp-thirdPreferenceLocation"
                        />
                        <small
                          v-if="i18n.form.wcag.selectScreenReaderHelp"
                          id="selectHelp-thirdPreferenceLocation"
                          class="sr-only"
                        >
                          {{ i18n.form.wcag.selectScreenReaderHelp }}
                        </small>
                        <kf-loader
                          v-if="isLoading"
                          loader-size="sm"
                          loader-color="primary"
                        />
                      </fieldset>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </transition>
        </div>

        <div class="col-span-12">
          <FormulateInput
            name="alreadyClient"
            type="checkbox"
          >
            <template #label="{ id }">
              <label
                :for="id"
                v-html="i18n.form.labels.alreadyClient"
              />
            </template>
          </FormulateInput>

          <FormulateInput
            name="agree"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.agree"
          >
            <template #label="{ id }">
              <label
                :for="id"
                v-html="i18n.form.labels.agree"
              />
            </template>
          </FormulateInput>
        </div>
      </fieldset>
    </wizard-step>
    <modal
      v-show="modalOpen"
      :modal="true"
      class="language-modal"
      hide-close-button
      @close="modalOpen = false"
    >
      <template slot="body">
        <div class="hc-stage-content">
          <h2
            v-if="i18n.form.labels.registerComplete.title"
          >
            {{ i18n.form.labels.registerComplete.title }}
          </h2>
          <!-- eslint-disable vue/no-v-html -->
          <div class="hc-description">
            {{ i18n.form.labels.registerComplete.paragraph.replaceAll("{childName}" , formData.myChild.firstName) }}
          </div>
          <div class="flex flex-col lg:flex-row gap-6">
            <KfButtonContainer
              :button-container-links="registerCompleteLinks"
              :button-container-position-horizontal="true"
            />

            <KfButton
              class="btn"
              type="primary"
              @click="registerAnotherChild"
            >
              {{ i18n.form.labels.registerComplete.anotherChildButton }}
            </KfButton>
          </div>
          <slot name="locator" />
        </div>
      </template>
    </modal>
    <slot name="wizard-application-footer" />
  </wizard>
</template>

<script>
import KfButton from '@/components/base/button/Button.vue';
import KfButtonContainer from '@/components/button-container/ButtonContainer.vue';
import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
import FieldsGender from '@/components/forms/fields/FieldsGender.vue';
import KfLoader from '@/components/loader/Loader';
import Modal from '@/components/modal/Modal.vue';
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIndicator from '@/components/wizards/components/WizardStepIndicator.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import getQueryParams from '@/lib/formulate/getFormQueryParam';
import { setDayCareString, trackEvent } from '@/lib/gtm/trackEvent';
import Utils from '@/lib/utils';
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'WizardApplication',
  components: {
    ChevronDown: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ChevronDown.vue'),
    Modal,
    Wizard,
    WizardStep,
    WizardStepIntro,
    WizardStepIndicator,
    FieldsGender,
    FieldsDatePicker,
    KfButtonContainer,
    KfButton,
    KfLoader
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    location: {
      type: Object,
      default: null,
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
    registerCompleteLinks: {
      type: Array,
      default: null,
    },
    supportsVso: {
      type: Boolean,
      default: false,
    },
    childcareType: {
      type: Object,
      default: null,
    },
    apiHostName: {
      type: String,
      default: '',
    },
    allowAlternativeLocations: {
      type: Boolean,
      default: true,
    },
    formToken: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        personalDetails: {},
        myChild: {},
        preferences: {},
        vsoSupport: ['After school care'],
      },
      alternativeLocationData: [],
      alternativeCityOptions: [],
      alternativeLocationOptions: { second: null, third: null },
      request: {
        isSending: false,
        errorSending: false,
      },
      modalOpen: false,
      showAlternativePreferences: false,
      isLoading: false,
      steps: [
        {
          key: 'personalDetails',
          name: this.i18n.form.steps.personalDetails,
          completed: false,
          active: false,
        },
        {
          key: 'myChild',
          name: this.i18n.form.steps.myChild,
          completed: false,
          active: false,
        },
        {
          key: 'preferences',
          name: this.i18n.form.steps.preference,
          completed: false,
          active: false,
        },
      ],
    };
  },
  computed: {

    showVso() {
      return this.supportsVso ?
              {
                Preschool: this.i18n.wizardSteps.preferences.vsoSupport.preschool,
                'After school care': this.i18n.wizardSteps.preferences.vsoSupport.afterschool,
              }
              : { 'After school care': this.i18n.wizardSteps.preferences.vsoSupport.afterschool,}
    },
    requireSecondOption() {
      return this.formData.preferences.thirdPreferenceLocation ? 'required' : '';
    },
    onlyPreschoolSelected() {
      return this.formData.vsoSupport[0] === 'Preschool' && this.formData.vsoSupport.length === 1;
    },
    datePickerValidationAfterSchoolCare() {
      return this.childcareType.childcareTypeKey === 'After school care'
        ? `bail|required|after:${Utils.date.getDate(new Date(), 0, 0, -14)}`
        : null;
    },
    datePickerValidationOthers() {
      return this.childcareType.childcareTypeKey !== 'After school care'
        ? `bail|required|after:${this.dateBeforeMinDateOfBirth}|before:${this.maximumApplicableDateOfChild}`
        : null;
    },
    maximumApplicableDateOfChild() {
      return `${Utils.date.getDate(new Date(this.formData?.myChild?.dateOfBirth), 0, 0, 14)}`;
    },
    daysOptions() {
      const daysOptions = {
        monday: this.i18n.form.labels.daysoptions.monday,
        tuesday: this.i18n.form.labels.daysoptions.tuesday,
        wednesday: this.i18n.form.labels.daysoptions.wednesday,
        thursday: this.i18n.form.labels.daysoptions.thursday,
        friday: this.i18n.form.labels.daysoptions.friday,
      };

      // if unknown is set, add it
      if (typeof this.i18n.form.labels.daysoptions.unknown !== 'undefined') {
        daysOptions.unknown = this.i18n.form.labels.daysoptions.unknown;
      }

      return daysOptions;
    },
    startOfCareValidation() {
      return `bail|required|after:${this.formData?.myChild?.dateOfBirth ||
        Utils.date.getDate(new Date())}`;
    },
    startOfCareMinDate() {
      return `${Utils.date.getDate(new Date(), 1)}`;
    },
    dateBeforeMinDateOfBirth() {
      return Utils.date.getDate(new Date(), 0, 0, -4);
    },
    filledInDateOfBirthIsMoreThan4YearsAgo() {
      if (this.formData.myChild?.dateOfBirth) {
        return moment(this.formData.myChild.dateOfBirth).isSameOrBefore(
          moment(this.dateBeforeMinDateOfBirth),
        );
      }
      return false;
    },
    translatedChildGender() {
      return this.i18n.form.labels.genderoptions[this.formData.myChild.gender];
    },
    translatedSelectedDays() {
      return this.formData.preferences['days[]']?.map(
        (dayKey) => this.i18n.form.labels.daysoptions[dayKey],
      );
    },
    translatedSelectedSalutation() {
      return this.i18n.form.labels.salutationOptions[this.formData.personalDetails.salutation];
    },
    gtmData() {
      return {
        event: 'registration',
        event_name: 'registration_progress',
        event_category: 'registration',
        event_action: 'registration_preferences',
        event_label: 'schrijf je kind in',
        childcare_type: this.childcareType.childcareTypeName,
        location: this.formData.personalDetails.zipcode,
        school: this.location.locationAddress || this.location.locationName,
      };
    },
    secondGtmEvent() {
        return {
          'event': 'registration',
          'event_name': 'registrationPersonalData',
          'event_category': 'registrationFlow',
          'event_action': 'registrationPersonalData',
          'childcare_type': setDayCareString(this.childcareType.childcareTypeKey),
          'location_province': this.location.locationProvince,
          'location_place': this.location.locationCity,
          'location_adress': this.location.locationAddress,
          'mdm_id': this.location.locationMdmId,
        }
    }
  },
  async created() {
    this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }

    if (this.allowAlternativeLocations) {
      this.alternativeLocationData = await this.getAlternativeLocations();
      this.alternativeCityOptions = this.alternativeLocationData.cities.map((city) => city.name);
      this.alternativeCityOptions.unshift('');
    }
  },
  methods: {
    checkSelectedCities() {
      return this.alternativeLocationData.cities
        .filter(
          (city) =>
            city.name === this.location.locationCity ||
            city.name === this.formData.preferences.secondPreferenceCity ||
            (this.formData.preferences.thirdPreferenceCity &&
              city.name === this.formData.preferences.thirdPreferenceCity),
        )
        .map((c) => {
          return {
            name: c.name,
            hasLocations:
              c.locations.length > 0 &&
              c.locations.some(
                (location) =>
                  location.address1 !== this.formData.preferences.thirdPreferenceLocation &&
                  location.address1 !== this.formData.preferences.secondPreferenceLocation &&
                  location.address1 !== this.location.locationAddress,
              ),
          };
        });
    },
    filteredCityOptions(currentCity) {
      const selectedOptions = this.checkSelectedCities();
      return this.alternativeCityOptions.filter((option) => {
        if (option === currentCity) return true;
        if (selectedOptions.some((o) => o.name === option)) {
          return selectedOptions.find((obj) => obj.name === option).hasLocations;
        }
        return true;
      });
    },
    triggerGtm(action) {
      // Google Tag Manager data
      trackEvent({ ...this.gtmData, event_action: action }, this.$gtm.enabled());

      // Second Track event
      const secondGtmData = this.secondGtmEvent;

      if ( action === 'registration_child_data') {
        secondGtmData.gender = this.translatedChildGender;
        secondGtmData.event_name = 'registrationMyChild';
        secondGtmData.event_action = 'registrationMyChild';
      }

      trackEvent({
        ...secondGtmData,
      }, this.$gtm.enabled());

    },
    getAlternativeLocations() {
      return new Promise((resolve, reject) => {
        let config = {};
        this.isLoading = true;

        const requestVerificationToken = document.body.querySelector(
          'input[type=hidden][name=__RequestVerificationToken]',
        )?.value;
        if (requestVerificationToken) {
          config = {
            headers: {
              'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
            },
          };
        }

        axios
          .get(
            `${this.apiHostName}/umbraco/api/location/All/ByCity?care-type=${this?.childcareType?.childcareTypeKey}&exclude-smallsteps-locations=false&registration-can-be-disabled=false`,
            config,
          )
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    onchangeSecondPreferenceCity(e) {
      const city = this.onchangePreferenceCity(e.target.value);
      if (city && city !== '') {
        this.alternativeLocationOptions.second = city.locations
          .map((location) => {
            return {
              label: location.address1,
              value:
                location.locationName !== ''
                  ? location.locationName + '|' + location.mdmId
                  : location.address1 + '|' + location.mdmId,
            };
          })
          .filter((item) => item.label !== this.location.locationAddress)
          .sort((a,b) => {
            const nameA = a.label.toUpperCase(); // Convert to uppercase for case-insensitive sorting
            const nameB = b.label.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });
      } else {
        this.alternativeLocationOptions.second = {};
        this.formData.preferences.secondPreferenceLocation = null;
      }
    },
    onchangeThirdPreferenceCity(e) {
      const city = this.onchangePreferenceCity(e.target?.value || e);
      if (city && city !== '') {
        this.alternativeLocationOptions.third = city.locations
          .map((location) => {
            return {
              label: location.address1,
              value:
                location.locationName !== ''
                  ? location.locationName + '|' + location.mdmId
                  : location.address1 + '|' + location.mdmId,
            };
          })
          .filter(
            (item) =>
              item.value !== this.formData.preferences.secondPreferenceLocation &&
              item.label !== this.location.locationAddress,
          ).sort((a,b) => {
            const nameA = a.label.toUpperCase(); // Convert to uppercase for case-insensitive sorting
            const nameB = b.label.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });
      } else {
        this.alternativeLocationOptions.third = {};
        this.formData.preferences.thirdPreferenceLocation = null;
      }
    },
    onChangeSecondPreferedLocation() {
      this.onchangeThirdPreferenceCity(this.formData.preferences.thirdPreferenceCity || '');
    },
    onchangePreferenceCity(value) {
      return this.alternativeLocationData.cities.find((city) => {
        return city.name === value;
      });
    },
    toggleAlternativePreferences() {
      this.showAlternativePreferences = !this.showAlternativePreferences;
    },
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      // Google Tag Manager data
      const { alreadyClient, startOfCare } = this.formData.preferences;

      trackEvent(
        {
          ...this.gtmData,
          user: alreadyClient,
          start_date: startOfCare,
          count_days: this.translatedSelectedDays.length,
        },
        this.$gtm.enabled(),
      );

      const requestVerificationToken = document.body.querySelector(
        'input[type=hidden][name=__RequestVerificationToken]',
      )?.value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        },
      };

      const dataToPost = {
        website: this.formData.personalDetails.website || '',
        bonusCode: this.formData.personalDetails.bonusCode || '',
        careType: this?.childcareType?.childcareTypeKey,
        desiredCareTypes: this.formData?.vsoSupport,
        formToken: this.formToken || '',
        locale: this.locale,
        locationName: this?.location?.locationName || '',
        locationEmailAddress: this?.location?.locationEmailAddress || '',
        locationAddress: this?.location?.locationAddress || '',
        locationAddress2: this?.location?.locationAddress2 || '',
        locationPostalCode: this?.location?.locationPostalCode || '',
        locationCity: this?.location?.locationCity || '',
        locationLmsId: this?.location?.locationLmsId || '',
        locationMdmId: this?.location?.locationMdmId || '',
        isPartouLocation: this?.location?.locationIsPartouLocation === 'true' ? true : false,
        school: this.formData?.myChild?.school || '',
        parent: {
          title: this.translatedSelectedSalutation,
          firstName: this.formData.personalDetails.firstname,
          initials: this.formData.personalDetails.initials,
          preposition: this.formData.personalDetails?.prefix || '',
          surname: this.formData.personalDetails.lastname,
          emailAddress: this.formData.personalDetails.email,
          telephoneNumber: this.formData.personalDetails?.phoneNumber || '',
          address: {
            street: this.formData.personalDetails.streetname,
            houseNumber: this.formData.personalDetails.housenumber,
            addition: this.formData.personalDetails.suffix,
            postalCode: this.formData.personalDetails.zipcode,
            city: this.formData.personalDetails.residence,
          },
          employed: this.formData?.personalDetails?.employed === 'true' ? true : false,
        },
        child: {
          gender: this.translatedChildGender,
          firstName: this.formData.myChild?.firstName || '',
          preposition: this.formData.myChild?.namePrefix || '',
          surname: this.formData.myChild.lastName,
          dateOfBirth: this.formData.myChild.dateOfBirth,
          bsn: this.formData?.myChild?.bsn || '',
          vve: this.formData?.myChild?.vve === 'true' ? true : false,
          school: this.formData?.myChild?.school || '',
        },
        startDate: this.formData.preferences.startOfCare,
        days: this.translatedSelectedDays,
        comments: this.formData.preferences?.remark || '',
        secondPreferenceLocation: {
          city: this.formData.preferences?.secondPreferenceCity || '',
          school: this.formData.preferences?.secondPreferenceLocation || '',
        },
        thirdPreferenceLocation: {
          city: this.formData.preferences?.thirdPreferenceCity || '',
          school: this.formData.preferences?.thirdPreferenceLocation || '',
        },
        isClient: this.formData.preferences?.alreadyClient ? true : false,
        acceptPrivacy: this.formData.preferences?.agree ? true : false,
      };

      axios
        .post(`${this.apiHostName}/umbraco/api/ApplicationForm/Submit`, dataToPost, config)
        .then(() => {
          // First hash email
          return Utils.hash.sha256(this.formData.personalDetails?.email || '');
        })
        .then((hashedMail) => {
          // Then hash phone
          return Utils.hash.sha256(this.formData.personalDetails?.phoneNumber || '')
            .then((hashedPhone) => ({ hashedMail, hashedPhone }));
        })
        .then(({ hashedMail, hashedPhone }) => {
          // Now fire all tracking events with hashed values
          trackEvent(
            {
              event: 'registration',
              event_name: 'registration_completed',
              event_category: 'registration',
              event_action: 'registration_completed',
              event_label: 'schrijf je kind in',
              childcare_type: this.childcareType.childcareTypeName,
              location: this.formData.personalDetails.zipcode,
              school: this.location.locationAddress || this.location.locationName,
              count_days: this.translatedSelectedDays.length,
              type: 'old method',
            },
            this.$gtm.enabled(),
          );
          trackEvent({
            ...this.secondGtmEvent,
            gender:  this.translatedChildGender,
            'event_name': 'registrationMyPreferences',
            'event_action': 'registrationMyPreferences',
            'event_category': 'registration',
            days_chosen:  this.translatedSelectedDays,
          }, this.$gtm.enabled());
          trackEvent({
            ...this.secondGtmEvent,
            gender:  this.translatedChildGender,
            'event_name': 'registrationCompleted',
            'event_action': 'registrationCompleted',
            'event_category': 'registration',
            days_chosen:  this.translatedSelectedDays,
            mail: hashedMail,
            phoneNumber: hashedPhone,
          }, this.$gtm.enabled());
          this.modalOpen = true
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
    registerAnotherChild() {
      this.formData.myChild = {};
      this.$formulate.reset('myChild')
      this.$refs.wizard.currentStep = 'myChild';
      this.request.isSending = false;
      this.steps[2].completed = false
      this.modalOpen = false;
    },
    beforeEnter(el) {
      el.style.height = '0';
    },
    enter(el) {
      el.style.height = el.scrollHeight + 'px';
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px';
    },
    leave(el) {
      el.style.height = '0';
    },
  },
};
</script>
